"use client";

/**
 * Stack Modal Hook
 * 
 * Provides a backward-compatible hook for modal management that can work
 * with both the new global stack system and the legacy individual modal system.
 * This allows for gradual migration without breaking existing components.
 */

import * as React from "react";
import { useGlobalModalStack } from "../components/global-modal-stack";

interface UseStackModalOptions {
  modalId: string;
  fallbackToLocal?: boolean; // If true, falls back to local state if global stack not available
}

interface UseStackModalReturn {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggle: () => void;
  // For compatibility with existing ResponsiveModal components
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const useStackModal = ({ 
  modalId, 
  fallbackToLocal = true 
}: UseStackModalOptions): UseStackModalReturn => {
  // Try to use global stack first
  const [useGlobalStack, setUseGlobalStack] = React.useState(true);
  const [localOpen, setLocalOpen] = React.useState(false);
  
  let globalStack: ReturnType<typeof useGlobalModalStack> | null = null;
  let isGlobalModalOpen = false;
  let globalOpenModal: (() => void) | null = null;
  let globalCloseModal: (() => void) | null = null;
  
  try {
    if (useGlobalStack) {
      globalStack = useGlobalModalStack();
      isGlobalModalOpen = globalStack.isModalOpen(modalId as any);
      globalOpenModal = () => globalStack!.openModal(modalId as any);
      globalCloseModal = () => globalStack!.closeModal(modalId as any);
    }
  } catch (error) {
    // Global stack not available, fall back to local state
    if (fallbackToLocal) {
      setUseGlobalStack(false);
    } else {
      throw error;
    }
  }
  
  // Use global stack if available, otherwise use local state
  const isOpen = useGlobalStack && globalStack ? isGlobalModalOpen : localOpen;
  
  const onOpen = React.useCallback(() => {
    if (useGlobalStack && globalOpenModal) {
      globalOpenModal();
    } else {
      setLocalOpen(true);
    }
  }, [useGlobalStack, globalOpenModal]);
  
  const onClose = React.useCallback(() => {
    if (useGlobalStack && globalCloseModal) {
      globalCloseModal();
    } else {
      setLocalOpen(false);
    }
  }, [useGlobalStack, globalCloseModal]);
  
  const onToggle = React.useCallback(() => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }, [isOpen, onOpen, onClose]);
  
  const onOpenChange = React.useCallback((open: boolean) => {
    if (open) {
      onOpen();
    } else {
      onClose();
    }
  }, [onOpen, onClose]);
  
  return {
    isOpen,
    onOpen,
    onClose,
    onToggle,
    // For compatibility with existing ResponsiveModal components
    open: isOpen,
    onOpenChange,
  };
};
